import type { QueryClient } from "@tanstack/react-query";

export const authenticationExpiredEvent = "vagadmin:authentication-expired";
export const authenticationGenerationStorageKey = "vagadmin:authentication-generation";
const authenticationBroadcastChannel = "vagadmin:authentication";

let fallbackGeneration = "initial";
let fallbackSequence = 0;

const authenticatedSessionStoragePrefixes = [
  "vagadmin:",
  "tonkm-transfer-success:",
];

function clearAuthenticatedSessionStorage() {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key && authenticatedSessionStoragePrefixes.some((prefix) => key.startsWith(prefix)))
        sessionStorage.removeItem(key);
    }
  } catch {
    // Query data and rendered authorization still clear fail-closed if a
    // privacy mode denies storage access. Unrelated storage is never touched.
  }
}

export function captureAuthenticationGeneration() {
  try {
    const stored = globalThis.localStorage?.getItem(authenticationGenerationStorageKey);
    if (stored) fallbackGeneration = stored;
  } catch {
    // Some privacy modes expose localStorage but throw on access.
  }
  return fallbackGeneration;
}

function nextAuthenticationGeneration() {
  try {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Fall through to getRandomValues.
  }
  try {
    const bytes = crypto.getRandomValues(new Uint32Array(4));
    return [...bytes].map((value) => value.toString(16).padStart(8, "0")).join("-");
  } catch {
    fallbackSequence += 1;
    return `local-${Date.now().toString(36)}-${fallbackSequence.toString(36)}`;
  }
}

function broadcastAuthenticationChange(generation: string) {
  try {
    const channel = new BroadcastChannel(authenticationBroadcastChannel);
    channel.postMessage(generation);
    channel.close();
  } catch {
    // localStorage is the ordinary cross-tab mechanism. If both mechanisms
    // are unavailable, this document still clears itself fail-closed and
    // other tabs revalidate on their next authenticated request.
  }
}

/** Notify every reachable tab that the shared cookie identity has changed. */
export function publishAuthenticationChange() {
  const generation = nextAuthenticationGeneration();
  fallbackGeneration = generation;
  try {
    localStorage.setItem(authenticationGenerationStorageKey, generation);
  } catch {
    // BroadcastChannel provides the cross-tab fallback where available.
  }
  broadcastAuthenticationChange(generation);
  return generation;
}

export function listenForAuthenticationChanges(onChange: () => void) {
  try {
    const channel = new BroadcastChannel(authenticationBroadcastChannel);
    channel.addEventListener("message", (event) => {
      if (typeof event.data === "string" && event.data !== fallbackGeneration) {
        fallbackGeneration = event.data;
        onChange();
      }
    });
    return () => channel.close();
  } catch {
    return () => undefined;
  }
}

/** Remove every identity- and tenant-owned projection before changing users. */
export async function clearAuthenticatedCache(queryClient: QueryClient) {
  const cancellation = queryClient.cancelQueries();
  queryClient.clear();
  clearAuthenticatedSessionStorage();
  await cancellation;
}

export function signalAuthenticationExpired(expectedGeneration: string) {
  if (captureAuthenticationGeneration() !== expectedGeneration) return;
  publishAuthenticationChange();
  window.dispatchEvent(new Event(authenticationExpiredEvent));
}
